/* eslint-disable @next/next/no-img-element */
import useAnalytics from "app/analytics/hooks/useAnalytics"
import { ColorBlur } from "../ColorBlur"
import { HoverSkew } from "../HoverSkew"
import { BookDemoButton, SignUpButton } from "../Main"
import { Heading, LargeHeading, Paragraph, Section } from "../misc"
import Hero from "public/marketing/campaigns.png"
import { ReactNode } from "react"
import { SIcon, SIconType } from "@superwall-me/sui"

const freeFeatures = [
  "250 Conversions a Month",
  "Drag 'n Drop Paywall Editor",
  "200+ Paywall Templates",
  "Unlimited A/B tests",
  "Charts & Analytics",
]

const midFeatures = [
  "5,000 Conversions a Month",
  "Drag 'n Drop Paywall Editor",
  "200+ Paywall Templates",
  "Unlimited A/B tests",
  "Charts & Analytics",
]

const proFeatures = [
  "Unlimited Conversions",
  "We Build Your Paywalls",
  "4 Weekly Growth Meetings",
  "Dedicated Slack Channel",
  "Custom Integrations",
]

const Check = (
  <svg
    className="h-7 w-6 flex-none text-brand-primary"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
      clipRule="evenodd"
    ></path>
  </svg>
)

const PricingCard = ({
  title,
  description,
  featureTitle,
  features,
  planType,
  blogPost,
  icon = SIconType.Check,
  color = "text-brand-primary",
  children,
}: {
  title: string | ReactNode
  planType: string | ReactNode
  description: ReactNode
  featureTitle: string
  color?: string
  icon?: SIconType
  features?: string[]
  blogPost?: boolean
  children?: ReactNode
}) => (
  <HoverSkew borderRadius="16px" glowStength="extra-light">
    <div className="flex items-center justify-center py-9 pb-5 md:py-10 md:pb-5 lg:py-10 lg:pb-7 px-6 md:px-6 lg:px-8 pr-5">
      <div className="w-full flex flex-col items-start justify-start gap-4 md:gap-3 lg:gap-4">
        <div className={`${color} font-bold whitespace-nowrap -mt-4`}>{planType}</div>
        <div className="text-3xl lg:text-4xl font-bold mb-0 tracking-tight">{title}</div>
        <div className="font-base leading-6 tracking-normal">{description}</div>
        {children}
        {features && (
          <div className="w-full flex gap-4 md:gap-3 lg:gap-4 items-center justify-center">
            <p className={`${color} py-3 pt-0 lg:pt-3 font-bold text-lg whitespace-nowrap`}>
              {featureTitle}
            </p>
            <div className="h-[1px] w-full bg-white opacity-7"></div>
          </div>
        )}
        <ul
          role="list"
          className="grid grid-cols-1 gap-2 md:gap-4 leading-6 sm:grid-cols-1 sm:gap-3"
        >
          {features &&
            features.map((feature) => (
              <li key={feature} className="flex gap-x-3 items-center">
                <SIcon type={icon} className={color} size={16} />
                <span className="text-lg lg:text-base xl:text-lg lg:tracking-tight text-left">
                  {feature}
                </span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  </HoverSkew>
)

export const Pricing = ({ blogPost }: { blogPost?: boolean }) => {
  return (
    <Section className="text-left">
      <div id="pricing-anchor" className="absolute top-[-160px]"></div>
      <div className="flex flex-col gap-1 text-center items-center mb-5">
        <div className="!max-w-[750px] flex flex-row items-center justify-center gap-3 font-medium !text-lg !text-opacity-100 bg-brand-primary bg-opacity-7 p-7 py-2 rounded-full text-brand-primary mb-5 border border-2 border-brand-primary border-opacity-10">
          Start for FREE
        </div>
        <LargeHeading>{`Simple win-win pricing`}</LargeHeading>
        <Paragraph className="!max-w-[750px] !text-opacity-60">{`Interest aligned pricing. Contact us for a discount.`}</Paragraph>
      </div>

      <div className="w-full flex flex-col md:grid md:grid-cols-6 gap-3 max-w-8xl">
        <div className="col-span-3 lg:col-span-2">
          <PricingCard
            planType="Indie"
            title="Free"
            featureTitle={`Standard Features`}
            description={
              <>
                <span className="text-lg md:text-base xl:text-lg">
                  Up to 250 conversions per month
                </span>
                <br />
                <span className="my-2 opacity-60">Access to every standard feature</span>
              </>
            }
            features={freeFeatures}
            blogPost={blogPost}
          >
            <SignUpButton
              source={blogPost ? "blog-post" : "pricing-cta"}
              className="!my-2 !-ml-2 !px-10 !bg-brand-primary/7 !text-brand-primary !border !border-2 !border-brand-primary/10"
              mainCta={"Try it free"}
            ></SignUpButton>
          </PricingCard>
        </div>
        <div className="col-span-3 lg:col-span-2">
          <PricingCard
            planType="Startup"
            title={
              <>
                $0.20
                <span className="tracking-tight opacity-50 text-2xl">/conversion</span>
              </>
            }
            featureTitle={`Standard Features`}
            description={
              <>
                <span className="text-lg md:text-base xl:text-lg">
                  Pay as you go pricing that scales
                </span>
                <br />
                <span className="my-2 opacity-50">Up to 5,000 conversions a month</span>
              </>
            }
            features={midFeatures}
            blogPost={blogPost}
          >
            <SignUpButton
              source={blogPost ? "blog-post" : "pricing-cta"}
              className="!my-2 !-ml-2 !px-10 !bg-brand-primary/7 !text-brand-primary !border !border-2 !border-brand-primary/10"
              mainCta={"Sign Up"}
            ></SignUpButton>
          </PricingCard>
        </div>
        <div className="col-span-6 lg:col-span-2">
          <PricingCard
            icon={SIconType.Check}
            color="text-[#FFC459]"
            planType="Growth"
            title={<>Flat-Rate</>}
            featureTitle={`Premium Features`}
            description={
              <>
                <span className="text-lg md:text-base xl:text-lg">
                  <span className="underline">100% custom</span> flat-rate pricing
                </span>
                <br />
                <span className="my-2 opacity-60">Terms that make sense for you</span>
              </>
            }
            features={proFeatures}
            blogPost={blogPost}
          >
            <BookDemoButton
              source={blogPost ? "blog-post" : "pricing-cta"}
              className="!my-2  !-ml-2 !px-10 !bg-[#FFC459] !border !border-2 !border-[#FFC459]"
              mainCta={"Get Pricing"}
            ></BookDemoButton>
          </PricingCard>
        </div>
      </div>
    </Section>
  )
}
